import styled from '@emotion/styled';
import { useState, Fragment, useRef, useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';

import type { ProblemItemFreeTextbooks_problemContent$key } from '__free_textbooks_generated__/ProblemItemFreeTextbooks_problemContent.graphql';
import PreviewProblemAttachment from 'ms-components/PreviewProblem/components/PreviewProblemAttachment';
import PreviewProblemInstruction from 'ms-components/PreviewProblem/components/PreviewProblemInstruction';
import { ProblemDifficultyLabel } from 'ms-components/PreviewProblem/components/ProblemDifficultyLabel';
import { ProblemDurationLabel } from 'ms-components/PreviewProblem/components/ProblemDurationLabel';
import PreviewProblemSubproblem from 'ms-components/PreviewProblem/components/Subproblems/PreviewProblemSubproblem';
import { isThereInputToRender } from 'ms-components/PreviewProblem/components/Subproblems/SubproblemInput';
import TriangleDownIcon from 'ms-components/icons/TriangleDown';
import {
  PROBLEM_PADDING,
  EXPAND_BUTTON_WIDTH,
  EXPAND_BUTTON_SPACER_WIDTH,
} from 'ms-pages/Textbooks/components/ProblemItem/constants';
import { observeAndSetWidth } from 'ms-pages/Textbooks/components/ProblemItem/helpers';
import {
  ProblemWrapper,
  AttributesWrapper,
  InnerAttributesWrapper,
  problemStyles,
} from 'ms-pages/Textbooks/components/ProblemItem/sharedStyles';
import { fontFamily, fontWeight } from 'ms-styles/base';
import { colors } from 'ms-styles/colors';
import { BASE_UNIT } from 'ms-styles/theme/Numero';
import Button from 'ms-ui-primitives/Button';
import { HSpacer, HStack, VStack } from 'ms-ui-primitives/Stack';
import { styledHorizontallyScrollable } from 'ms-utils/emotion';
import { getSubproblemIndex } from 'ms-utils/misc/getIndex';
import type { FixGeneratedKey } from 'ms-utils/relay/relay-compiler-language-typescript-fixes';

export const INDEX_BUBBLE_SIZE = 24;
// These values are handpicked and will depend on the icon used for the toggle

const indexBubble = {
  alignItems: 'center',
  flexShrink: 0,
  borderRadius: '50%',
  display: 'flex',
  fontFamily: fontFamily.body,
  fontSize: INDEX_BUBBLE_SIZE / 2,
  fontWeight: fontWeight.semibold,
  height: INDEX_BUBBLE_SIZE,
  justifyContent: 'center',
  width: INDEX_BUBBLE_SIZE,
};

const SubproblemIndex = styled.div({
  ...indexBubble,
  backgroundColor: colors.ironLight,
  color: colors.grayChateau,
});

const SUBPROBLEM_CONTAINER_MARGIN_RIGHT = INDEX_BUBBLE_SIZE / 2;
const SUBPROBLEM_CONTAINER_WIDTH = INDEX_BUBBLE_SIZE;

const PROBLEM_CONTENT_FRAGMENT = graphql`
  fragment ProblemItemFreeTextbooks_problemContent on ProblemContent {
    id
    attachment
    difficultyLevel
    estimatedCompletionTime
    ...PreviewProblemInstruction_problemContent
    ...PreviewProblemAttachment_problemContent
    ...PreviewProblemAttributes_problemContent
    subproblems {
      instruction
      subproblemType
      ...PreviewProblemSubproblem_subproblem
    }
  }
`;

const SubproblemMarkerContainer = styled.div({
  marginRight: SUBPROBLEM_CONTAINER_MARGIN_RIGHT,
  // To force alignment with the subproblems
  transform: `translateY(${BASE_UNIT}px)`,
  width: SUBPROBLEM_CONTAINER_WIDTH,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginLeft: 6 * BASE_UNIT,
});

const Subproblem = styled.div({
  display: 'flex',
});

const VerticalLine = styled.div({
  height: '100%',
  borderLeft: `2px solid ${colors.ironLight}`,
  width: 0,
});

type Props = {
  problemContentKey: FixGeneratedKey<ProblemItemFreeTextbooks_problemContent$key>;
  startsCompressed: boolean;
};

export function ProblemItem({ problemContentKey, startsCompressed }: Props) {
  const problemContent = useFragment(
    PROBLEM_CONTENT_FRAGMENT,
    problemContentKey,
  );
  const [isCompressed, setCompressed] = useState(startsCompressed);
  const problemRef = useRef(null);
  const problemContentWrapperRef = useRef(null);
  const [problemWidth, setProblemWidth] = useState(0);

  // Subproblem parts and attachments are shown when expanded
  // so, it only makes sense to expand (and show the triangle) if at least one of those is present
  const hasSubproblemContent =
    problemContent.subproblems.some(
      ({ instruction, subproblemType }) =>
        (instruction !== null && instruction !== '') ||
        isThereInputToRender(subproblemType),
    ) || !!problemContent.attachment;

  // setting resize observers for the problem and the problem content to detect overflows
  useEffect(() => {
    if (problemRef.current == null) return;
    observeAndSetWidth({
      ref: problemRef,
      widthSetter: setProblemWidth,
    });
  }, [problemRef]);

  const canUseProblemWidth = problemWidth > 0;

  const problemContentWidth =
    problemWidth -
    PROBLEM_PADDING * 2 -
    (hasSubproblemContent
      ? EXPAND_BUTTON_WIDTH + EXPAND_BUTTON_SPACER_WIDTH
      : 0);

  return (
    <ProblemWrapper ref={problemRef}>
      <HStack style={{ padding: PROBLEM_PADDING }}>
        {hasSubproblemContent && (
          <>
            <Button
              color="neutralGray"
              padding={0}
              onClick={() => setCompressed(compressed => !compressed)}
              label={isCompressed ? 'Expand' : 'Collapse'}
              height={EXPAND_BUTTON_WIDTH}
            >
              <TriangleDownIcon
                aphroditeStyles={[
                  problemStyles.triangle,
                  isCompressed && problemStyles.triangleRight,
                  !isCompressed && problemStyles.chevronBottom,
                ]}
                size={16}
              />
            </Button>
            <HSpacer width={EXPAND_BUTTON_SPACER_WIDTH} />
          </>
        )}
        <VStack style={{ marginTop: '-8px' }} ref={problemContentWrapperRef}>
          <PreviewProblemInstruction
            problemContent={problemContent}
            // Problem instruction can overflow too, although in very rare cases.
            // So we need to set a specific width for it
            // so that horizontal overflow can be handled properly by child MathContent component
            width={canUseProblemWidth ? problemContentWidth : undefined}
          />
          {hasSubproblemContent && !isCompressed && (
            <VStack
              style={
                canUseProblemWidth
                  ? {
                      width: problemContentWidth,
                      ...styledHorizontallyScrollable,
                      // to prevent the vertical scrollbar from showing up unnecessarily
                      overflowY: 'hidden',
                    }
                  : {
                      // although I didn't see any content overflow on mount,
                      // it's better to prevent overflow on mount,
                      // until problem width is available
                      // as a precaution
                      overflowX: 'hidden',
                    }
              }
            >
              <PreviewProblemAttachment problemContent={problemContent} />
              {problemContent.subproblems.map((subproblem, idx, arr) => (
                <Subproblem key={idx}>
                  <SubproblemMarkerContainer>
                    {arr.length > 1 ? (
                      <Fragment>
                        <SubproblemIndex>
                          {getSubproblemIndex(idx)}
                        </SubproblemIndex>
                        {idx < arr.length - 1 ? <VerticalLine /> : null}
                      </Fragment>
                    ) : null}
                  </SubproblemMarkerContainer>
                  <PreviewProblemSubproblem subproblemKey={subproblem} />
                </Subproblem>
              ))}
            </VStack>
          )}
        </VStack>
      </HStack>
      <AttributesWrapper>
        <InnerAttributesWrapper>
          {problemContent.difficultyLevel != null && (
            <>
              <ProblemDifficultyLabel
                difficulty={problemContent.difficultyLevel}
              />
              <HSpacer width={4} />
            </>
          )}
          <ProblemDurationLabel
            duration={problemContent.estimatedCompletionTime}
          />
          <HSpacer width={16} />
        </InnerAttributesWrapper>
      </AttributesWrapper>
    </ProblemWrapper>
  );
}
